/**
 * buyerStats — pure helpers for ProfileScreen + StatsScreen.
 *
 * No React, no fetch, no DOM. Deterministic given inputs (clock injected as arg).
 *
 * Used by:
 *   - screens/StatsScreen (hero credit, bar chart, acceptance %)
 *   - screens/ProfileScreen (acceptance % — FTR-1032 parity)
 *   - lib/__tests__/buyerStats.test.ts
 */

// ─── Types (mirror backend shapes — L-240 inline-per-callsite is for SCREENS,
// not for lib helpers; see lib/format.ts header note) ───────────────────────
export interface StatsInput {
  approved_count: number;
  rejected_count: number;
  sla_cancelled_count: number;
  taken_slots_count: number;
}

export interface HistoryItem {
  type: string;             // 'credit' | 'withdraw' | 'refund' | 'adjustment'
  amount_kopecks: number;   // signed (withdraws are negative per backend SSOT)
  created_at: string;       // ISO 8601
}

export interface MonthBucket {
  label: string;     // 'Янв' | 'Фев' | ... (RU short month, nominative)
  kopecks: number;   // sum of credit amounts in that month
  isCurrent: boolean; // true ONLY for the bucket matching `now`
}

const RU_MONTHS_SHORT = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
] as const;

/**
 * Acceptance percentage = approved / taken (FTR-1233 formula).
 * Guard: approved_count === 0 → null → FE renders "—" (not "0%").
 * This includes in-flight tasks in denominator — acceptance % decreases while
 * tasks are in progress, which founder confirmed is OK behavior.
 *
 * @returns integer 0..100, or null when approved is zero ("—" UI affordance)
 */
export function computeAcceptancePercent(s: StatsInput): number | null {
  if (s.approved_count === 0) return null;
  if (s.taken_slots_count === 0) return null;
  return Math.round((s.approved_count / s.taken_slots_count) * 100);
}

/**
 * Group `credit` items into N consecutive monthly buckets ending at `now`'s month.
 * Withdraws / refunds / adjustments are ignored (StatsScreen shows EARNINGS only).
 *
 * Buckets returned oldest-first. `isCurrent=true` flags the last bucket (= `now`'s
 * month) regardless of value, so the bar chart always highlights "this month".
 *
 * @param items — full history (any order, any time range)
 * @param monthsBack — bucket count (≥1; design uses 5)
 * @param now — reference date (injectable for tests; defaults to current clock)
 */
export function aggregateByMonth(
  items: HistoryItem[],
  monthsBack: number,
  now: Date = new Date(),
): MonthBucket[] {
  const buckets: MonthBucket[] = [];
  const nowY = now.getUTCFullYear();
  const nowM = now.getUTCMonth(); // 0-based
  // Build buckets oldest-first
  for (let i = monthsBack - 1; i >= 0; i--) {
    // Compute year/month for this bucket (UTC)
    let y = nowY;
    let m = nowM - i;
    while (m < 0) { m += 12; y -= 1; }
    buckets.push({
      label: RU_MONTHS_SHORT[m],
      kopecks: 0,
      isCurrent: i === 0,
    });
  }
  // Window bounds in UTC: [start of oldest bucket, start of next month after now)
  let oldestY = nowY;
  let oldestM = nowM - (monthsBack - 1);
  while (oldestM < 0) { oldestM += 12; oldestY -= 1; }
  const windowStart = Date.UTC(oldestY, oldestM, 1);
  const windowEnd = Date.UTC(nowY, nowM + 1, 1);
  for (const it of items) {
    if (it.type !== 'credit') continue;
    const t = new Date(it.created_at).getTime();
    if (Number.isNaN(t) || t < windowStart || t >= windowEnd) continue;
    const d = new Date(t);
    const dY = d.getUTCFullYear();
    const dM = d.getUTCMonth();
    const monthsAgo = (nowY - dY) * 12 + (nowM - dM);
    const idx = monthsBack - 1 - monthsAgo;
    if (idx >= 0 && idx < monthsBack) {
      buckets[idx].kopecks += it.amount_kopecks;
    }
  }
  return buckets;
}

/**
 * Sum of `credit` amounts in the current calendar month of `now`.
 * Used by StatsScreen hero "+X ₽ в этом месяце".
 */
export function currentMonthCredits(items: HistoryItem[], now: Date = new Date()): number {
  const monthStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
  const monthEnd = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
  let sum = 0;
  for (const it of items) {
    if (it.type !== 'credit') continue;
    const t = new Date(it.created_at).getTime();
    if (Number.isNaN(t)) continue;
    if (t >= monthStart && t < monthEnd) sum += it.amount_kopecks;
  }
  return sum;
}
